import * as Sentry from "@sentry/react";

const warnedFallbacks = new Set<string>();

function warnDeletedWorkflowReviewCopyFallback(workflowId: number | null) {
  // Keyed on the workflow so one mismatched column cannot silence another's
  // report.
  const workflow = String(workflowId ?? "none");
  if (warnedFallbacks.has(workflow)) {
    return;
  }
  warnedFallbacks.add(workflow);
  // The status response carries `workflow_deleted` and column-scoped
  // copy together, so a missing review there is a contract mismatch.
  Sentry.captureException(
    new Error(
      "Using composed deleted workflow review copy because served copy is missing",
    ),
    {
      level: "warning",
      tags: {
        source: "analytics",
        operation: "deletedWorkflowCopyFallback",
        automationWorkflowId: workflow,
      },
    },
  );
}

/**
 * The two sentences the deleted state says, with what stands in for a payload
 * that does not carry the served ones.
 *
 * Both are authored server-side — `deleted_workflow_review_copy` and
 * `deleted_workflow_recorded_against` in `midship/heretic/analytics/copy.py` —
 * beside every other analytics wording. That module is the single authoring of
 * these strings for every renderer of them, and a constant declared in a React
 * component would be unreachable to the Python ones.
 *
 * `AnalyticsReviewSections` reads the served string and reaches these stand-ins
 * only where its half of the payload is null. The review paragraph tracks the
 * server's wording, since the two render into the same place; the recorded-
 * against line does not try to, because the served one states a date.
 */

/**
 * Why the evidence behind a standing result is gone, and what the auditor can
 * still do.
 *
 * The workflow is named by id and never by name: the only name Midship holds is
 * the free text captured when the column was authored, which an upstream rename
 * leaves behind, and the live lookup that would supply the real one is the call
 * answering `410 Gone` in this very state.
 */
export function deletedWorkflowReviewCopy(workflowId: number | null): string {
  // Dropped rather than rendered with a hole where the id goes. The sentence is
  // true without it, and a binding naming no workflow at all is not a state
  // this paragraph can describe more precisely than it already does.
  const named = workflowId === null ? "" : ` (workflow ${workflowId})`;
  return `The workflow that powered this test${named} was deleted in Optro Analytics after this test ran, so it can no longer produce output. The result above stands as recorded, but the step detail, input parameters and output files behind it were deleted with the workflow and cannot be recovered. Attach an existing workflow or build a new one to test this again. The rest of this control's workpaper is unaffected.`;
}

export function servedDeletedWorkflowReviewCopy(
  review: string | null | undefined,
  workflowId: number | null,
  { warnOnMissing = true }: { warnOnMissing?: boolean } = {},
): string {
  if (review !== null && review !== undefined) return review;
  if (warnOnMissing) {
    warnDeletedWorkflowReviewCopyFallback(workflowId);
  }
  return deletedWorkflowReviewCopy(workflowId);
}

/**
 * The WORKFLOW section's line where the manifest will never serve one: its read
 * failed or found no run, or the column names no workflow binding for the
 * server to describe.
 *
 * Neither a date nor a population, both of which the served line states. It
 * dates the run in one time zone so every reader sees the same day, and a date
 * rendered here would be in the reader's own zone. What remains is the one fact
 * the column's status already established.
 */
const DELETED_WORKFLOW_RECORD_UNAVAILABLE =
  "Optro Analytics no longer holds this workflow, so it cannot be opened or re-run.";

export function servedDeletedWorkflowRecordedAgainst(
  recordedAgainst: string | null | undefined,
): string {
  return recordedAgainst ?? DELETED_WORKFLOW_RECORD_UNAVAILABLE;
}
